import {
  BadgeCheck,
  Boxes,
  Building,
  Cable,
  ChartLine,
  Cloud,
  Coins,
  Crosshair,
  KeyRound,
  Landmark,
  Layers,
  MapPinned,
  Megaphone,
  Plug,
  Radar,
  Scale,
  Scaling,
  ShieldCheck,
  Sparkles,
  Store,
  UserRoundSearch,
  Users,
  Zap,
  type LucideIcon,
} from "lucide-react"

// The priority audiences, shared by the /solutions page (full sections with
// jobs), the landing-page teaser and the header's Solutions menu (name +
// one-liner only).
//
// Adding an audience is one entry in AUDIENCES: the section, the hero pill,
// the teaser card and the nav menu all render from this list. Per-audience
// pages (/solutions/retailers) are a later split, so keep `id` URL-shaped.
//
// Every job links into a real Docs guide — see the guides index for the full
// set: https://console.pine59.com/docs/guides
//
// v2 — closes the gap Anna flagged: the "who it's for" nav column
// (data/audiences-nav.ts) had six audiences, this page only had four, three
// of them stubs. "Consultancies & SaaS companies" splits into two real
// audiences (SaaS getting its own jobs, not just inheriting the old
// combined ones), and Marketing is new. Civic is deliberately NOT added yet
// ("maybe without civic for now") — it stays a nav-only stub until there's
// real content to back it, same rule as everywhere else in this rework:
// no page without primary evidence behind it.
//
// SaaS's jobs come from the real Docs: the "Using the platform" guides (Make
// your first query, Build with AI) and the Concepts page (pre-built Place /
// Area / Way location types, plus custom boundaries on request). There is
// deliberately no "match your own locations" job: matching works but is hard
// to do well, so it isn't something to sell.
//
// Marketing's jobs come from the Sales Playbook's real "Creating Audiences"
// walkthrough (Feature Summary: Foot Traffic Trends, Demographic &
// Psychographic Segmentation, Filters, Compare) — not invented.
//
// Consultancies' third job (previously it only had two once SaaS's job
// moved out) comes from the Location Insights deck's Retail Coach case
// study — a real consultancy client, market-level rather than
// single-retailer analysis.
//
// v3 — per Anna: "Restaurants and hospitality is kinda Retail, so that
// group absorbs Restaurants and Hospitality." The standalone audience is
// gone; Retail analytics' lede and its Site selection / Competitive
// benchmarking / White space jobs are broadened to fold in the
// format-aware language (daypart, drive-time, unit, franchise territory)
// that used to live in the separate Restaurants & hospitality section,
// rather than just deleting that content. Still no Civic, per the
// earlier call.
//
// v4 — per Anna: Retail analytics' old lede overclaimed ("shopping starts
// before the door") on ground that's actually RetailNext's, not Pine59's
// own. What Pine59 owns directly is the market around the store — so the
// lede now leads with that, and the proof card explicitly separates "the
// market" (Pine59) from "through the door" (RetailNext). Site selection and
// White space are dropped from the lead jobs in favour of the three jobs
// Anna named directly: Competitive benchmarking, Understand your audience,
// Activate on your audience — the last two grounded in Brand Comparison's
// real Audience Evolution / Psychographic Profiles sections and the same
// Sales Playbook "Creating Audiences" evidence Marketing's jobs use below,
// not invented for retail specifically.
//
// CRE's jobs are renamed to the terms Anna actually uses in the industry —
// Site selection (was "Underwrite the trade area") and Manage your
// portfolio (was "Monitor the asset over time," broadened from one asset to
// the whole portfolio) — same underlying evidence, clearer names.
//
// SaaS and Consultancies keep their jobs; only the lede/proof tone changed
// (trustworthy/compliant/always-something-for-your-location for SaaS;
// simple/flexible/repeatable-output for Consultancies), per Anna's direct
// wording.
//
// v5 — per Anna: the job cards used to lead with `reality`, a stake/problem
// line, before revealing `capability` on hover. That "stakes" framing read
// as counterproductive (gloomy by default, and the actually useful
// sentence stayed hidden until someone hovered), so it's gone. Each job is
// just `capability` now, shown straight away.
//
// v6 — per Anna: Civic, previously excluded for no primary evidence, gets a
// real section. Area Identification and Area Development Reporting are both
// tagged Urban Development in the real Docs catalog; the "Finding Denver's
// up-and-coming neighbourhoods," "Reporting the 16th Street reopening in
// Denver" and "Tracking Grünerløkka's district trend" examples are civic
// cases already. Jobs reuse the same real guides Consultancies/Marketing/CRE
// already point to (same report, reframed for a civic reader), the same
// pattern every other audience here follows. Jobs cover both districts and
// parks (Anna: civic clients are often parks departments), not just urban
// districts.
//
// v7 — per Anna: each blog post should back exactly one audience, not be
// spread thin across several. "Location data vs. location intelligence"
// (aggregated vs. device-level, choosing what data you need) narrowed to
// SaaS alone — its own lede already leads with "sourcing your own location
// data and compliance model," the post's exact topic; Marketing and
// Consultancies drop it rather than share a generic fit. "What is a
// catchment area?" narrowed to CRE alone — catchment is foundational to
// site selection/trade-area work there, more than it is to Civic's actual
// jobs (area scoring, before/after measurement, not catchment radius).

// Guide links: one guide, one job. A guide is the worked example for a specific
// card, not a general reference, so no guide is linked from two cards (or two
// audiences); a card with no matching guide simply has no link.
const DOCS = "https://console.pine59.com/docs/guides"
const DOCS_CONCEPTS = "https://console.pine59.com/docs/concepts"

export type Job = {
  name: string
  // Distinct per job (not the audience's own icon, repeated three times) —
  // picked to match what the job is actually about, per Anna's flag that
  // the same icon on every card in a row reads as decoration, not meaning.
  icon: LucideIcon
  // What Pine59 does for this job — an active, product-owned verb. Shown
  // directly on the card, not gated behind hover.
  capability: string
  // A real, specific Docs guide for this job — per Anna's call, Pine59
  // doesn't build separate internal capability pages (the old
  // /solutions/brand-comparison, /solutions/store-profiling concept pages
  // are gone), and a card with no real guide gets no link at all rather
  // than pointing at the generic guides index.
  guide?: string
  // The link text, when "See the guide" would be wrong — e.g. a card that is
  // about the whole collection of guides and points at the index.
  guideLabel?: string
  // Optional real product screenshot (path under /public), rendered as its
  // own big card immediately next to this job's card in the grid — Anna's
  // reference (Clay/Heidi's big, spacious imagery, not a small hover
  // teaser). Only set where a real, topically-relevant screenshot exists.
  bigImage?: string
  // When true, bigImage renders wider on desktop (sm:max-w-xl lg:max-w-2xl
  // instead of the default max-w-xs) — for an asset composed for that
  // wider canvas, rather than the usual tall/narrow screenshot crop.
  bigImageWide?: boolean
  // Where the interface sits across a wide bigImage: its centre, as a % of the
  // art's width. The art is 2000 x 933 with the interface near the middle but
  // not always dead centre; phones crop the image to its card (object-cover),
  // and this keeps each crop centred on its own interface. Defaults to 50.
  bigImageFocus?: number
}

export type Audience = {
  id: string
  icon: LucideIcon
  name: string
  // One line for the landing-page teaser card and the header menu. The
  // /solutions hero splits it on " · " to lay the terms out as a list. Terms,
  // not steps: they don't happen in order and a reader may only care about
  // one of them, so don't join them with arrows.
  teaser: string
  // The fuller intro on the /solutions section.
  lede: string
  jobs: Job[]
  // Blog post slugs (from data/posts.ts) surfaced as "From the blog" under
  // the section. Placeholder posts still link — they show "coming soon".
  readingSlugs?: string[]
  // Optional decorative illustration (path under /public), the backdrop of
  // the teaser/lede hero card at the top of the section — Anna's Figma art,
  // not every audience has one.
  introArt?: string
  // "light" (default) is art with a transparent top that bleeds in from the
  // bottom of a light card (Retail's landscape). "dark" is a full-bleed dark
  // illustration that fills the whole card, so the text on it goes light.
  // "pale" is the same full-bleed fill but light art, so the text stays dark.
  introArtTone?: "light" | "dark" | "pale"
  // Bottom-bleed art only: how tall the visible art is, as a percent of the
  // card's width (the art scales with the card, so a fixed padding would let
  // text run into it on wide screens). The hero keeps that much room under its
  // text, plus a gap.
  introArtClearance?: number
  // The landing-page card's own art: a forest-green, grainy composition with
  // the audience's clipped art in front (from the Figma "Big illustration"
  // frames), bottom-anchored on a forest card. Separate from introArt, which
  // is the transparent cutout used on the /solutions hero.
  landingArt?: string
}

export const AUDIENCES: Audience[] = [
  {
    id: "retail",
    icon: Store,
    name: "Retailers",
    teaser: "Benchmark · Understand · Activate",
    lede: "Benchmark against competitors, see who's actually visiting, where they live and go, and reach more of them, from aggregated, real-world movement data.",
    jobs: [
      {
        name: "Benchmark competitors",
        icon: Scale,
        capability:
          "Benchmark any location, or a whole brand's trajectory, against the competitors, category or market that matters. Understand what others are doing and spot market trends early.",
        guide: `${DOCS}/brand-comparison`,
        bigImage: "/product/retail-competitive-benchmarking-wide.webp",
        bigImageWide: true,
        bigImageFocus: 53.5,
      },
      {
        name: "Activate audiences",
        icon: Zap,
        capability:
          "See the demographic profile of who's actually visiting, trended year over year, then turn it into segments precise enough to hand straight to the next campaign. Ready to activate.",
        bigImage: "/product/retail-understand-activate-wide.webp",
        bigImageWide: true,
        bigImageFocus: 50,
      },
    ],
    readingSlugs: ["retailnext-partnership"],
    // Clipped city skyline on a transparent background, as cropped in the
    // Figma frame (1774x450, ~18% of the card's width tall). It fills the right
    // three quarters, under the centred description box, so the full 18.1 is
    // kept clear: the towers stay in full view.
    introArt: "/art/retail-hero.webp",
    introArtClearance: 18.1,
    landingArt: "/art/landing/retail.webp",
  },
  {
    id: "marketing",
    icon: Megaphone,
    name: "Marketers",
    teaser: "Define · Target · Prove",
    lede: "Marketing teams already know what happens online. Pine59 adds what happens in physical locations, from aggregated foot traffic: who visits, where they come from, and whether a campaign moved them.",
    jobs: [
      {
        name: "Define audiences",
        icon: UserRoundSearch,
        capability:
          "See who actually visits and where they come from, by demographics, psychographic segments and trade area, then add it to the audiences already built.",
        bigImage: "/product/marketers-define-audiences-wide.webp",
        bigImageWide: true,
        bigImageFocus: 52,
      },
      {
        name: "Target competitors",
        icon: Crosshair,
        capability:
          "See a competitor's customer profile and trade area, then bring it into the existing targeting. Competitor conquesting, built on who actually visits.",
        bigImage: "/product/marketers-target-competitors-wide.webp",
        bigImageWide: true,
        bigImageFocus: 55,
      },
      {
        name: "Prove campaign impact",
        icon: ChartLine,
        capability:
          "Compare foot traffic before and after a campaign, week by week, and prove its ROI in actual visits, not just impressions.",
        bigImage: "/product/marketers-prove-roi-wide.webp",
        bigImageWide: true,
        bigImageFocus: 53,
      },
    ],
    // Clipped flow contour on a transparent background (like Retail's
    // landscape). The art is tall across the whole centre (22% of the card's
    // width), so only 15 is kept clear (enough to leave the pins visible) and
    // the frosted description box floats over the rest of the pattern.
    introArt: "/art/marketing-hero.webp",
    introArtClearance: 15,
    landingArt: "/art/landing/marketing.webp",
  },
  {
    id: "saas",
    icon: Cloud,
    name: "SaaS",
    teaser: "Integrate · Cover · Build",
    lede: "No detour through sourcing location data. Integrate Pine59 once: test it through Console, then build on the same data through the API.",
    jobs: [
      {
        name: "Integrate the data",
        icon: Plug,
        capability:
          "Add location insights to the product through the API, starting with a first query.",
        guide: `${DOCS}/first-query`,
      },
      {
        name: "Cover the locations",
        icon: Layers,
        capability:
          "Places, Areas and Ways come pre-built. If that's not enough, we also set up custom locations.",
        guide: DOCS_CONCEPTS,
      },
      {
        name: "Build with AI",
        icon: Sparkles,
        capability:
          "Point Claude, Cursor or any AI client at the Pine59 docs and API to list datasets, query metrics and explain results.",
        guide: `${DOCS}/build-with-ai`,
      },
    ],
    readingSlugs: ["location-data-vs-location-intelligence"],
    // Pale green data-grid art clipped to a U-shaped contour on a transparent
    // background (like Retail's skyline): bleeds in from the bottom of a light
    // card, so no introArtTone. The wide valley is ~12% of the card's width
    // tall under the description box; the sides rise to ~20%.
    introArt: "/art/saas-hero.webp",
    introArtClearance: 12,
    landingArt: "/art/landing/saas.webp",
  },
  {
    id: "consultancies",
    icon: Users,
    name: "Consultancies",
    teaser: "Analyse · Connect · Defend",
    lede: "One foundation for whatever question comes next, from a single location to a whole market. Clear, repeatable output every time: simple enough to hand a client, flexible enough for any engagement.",
    jobs: [
      {
        name: "Analyse any scale",
        icon: Scaling,
        capability:
          "Look at a single location or a whole market, at whatever scale the question needs: nationwide, city, neighbourhood or trade area.",
        guide: `${DOCS}/analog-modeling/us-candidate-malls-colorado`,
      },
      {
        name: "Connect to tools",
        icon: Cable,
        capability:
          "Plug Pine59 into existing workflows with a plain API call from any HTTP client, or with no code at all.",
      },
      {
        name: "Defend the answer",
        icon: ShieldCheck,
        capability:
          "Back every number with foot traffic, trade areas and attributable visitor origins. A recommendation that holds up.",
      },
    ],
    // Data-grid art clipped to a two-humped hill on a transparent background:
    // bleeds in from the bottom of a light card, so no introArtTone. 15.2: the
    // hill is up to ~15% of the card's width tall, all of it under the
    // centred description box.
    introArt: "/art/consultancies-hero.webp",
    introArtClearance: 15.2,
    landingArt: "/art/landing/consultancies.webp",
  },
  {
    id: "commercial-real-estate",
    icon: Building,
    name: "Commercial Real Estate",
    teaser: "Select · Lease · Manage",
    lede: "Know how much foot traffic a site actually draws, where it comes from, and which way the trend is pointing, before acquiring or breaking ground, for one asset or a whole portfolio.",
    jobs: [
      {
        name: "Select a site",
        icon: MapPinned,
        capability:
          "Compare malls and sites on foot traffic: how much each draws and how it's growing. Then see the trade area behind the final pick.",
        guide: `${DOCS}/store-profiling`,
      },
      {
        name: "Lease and tenant mix",
        icon: KeyRound,
        capability:
          "Bring location intelligence into leasing conversations, whether negotiating rent or attracting a tenant.",
      },
      {
        name: "Manage the portfolio",
        icon: Boxes,
        capability:
          "Track foot traffic over time for every asset against comparable locations, so the right action is clear.",
        // The continuous-monitoring example, not Marketing's before/after
        // one — this job is about tracking an existing portfolio over time,
        // not evaluating a single intervention.
      },
    ],
    readingSlugs: ["what-is-a-catchment-area"],
    // Clipped skyline on a transparent background (like Retail's landscape).
    // 16: the spire at the far right is 23% of the card's width tall, but the
    // skyline is only ~15% tall under the centred description box.
    introArt: "/art/cre-hero.webp",
    introArtClearance: 16,
    landingArt: "/art/landing/cre.webp",
  },
  {
    id: "civic",
    icon: Landmark,
    name: "Public services",
    teaser: "Track · Prove · Invest",
    lede: "Every public investment shapes how people use a place. Make sure it's the right one, for the community it serves. Track how an area is developing, prove what an investment delivered, and invest where it matters most.",
    jobs: [
      {
        name: "Track an area",
        icon: Radar,
        capability:
          "Watch foot traffic in a district or park as it shifts, and spot a trend while there's still time to act.",
        // The continuous-monitoring example, same one CRE's "Manage your
        // portfolio" uses — same underlying report, a civic area (a
        // district, a park) instead of a portfolio of assets. "Area," not
        // "district," since Anna's civic clients are often parks.
        guide: `${DOCS}/area-development-reporting/no-grunerlokka-tracking`,
        bigImage: "/product/civic-track-area-wide.webp",
        bigImageWide: true,
        bigImageFocus: 50,
      },
      {
        name: "Prove the impact",
        icon: BadgeCheck,
        capability:
          "Show the community what a project actually did for the area. Compare foot traffic before and after, and prove the impact.",
        guide: `${DOCS}/area-development-reporting/us-16th-street-denver`,
        bigImage: "/product/civic-prove-impact-wide.webp",
        bigImageWide: true,
        bigImageFocus: 58,
      },
      {
        name: "Invest where it matters",
        icon: Coins,
        capability:
          "Find the places where an investment can change the dynamics of an area: where momentum is building and activity is already picking up.",
        guide: `${DOCS}/area-identification`,
        bigImage: "/product/civic-invest-wide.webp",
        bigImageWide: true,
        bigImageFocus: 50,
      },
    ],
    // Clipped skyline on a transparent background (like Retail's landscape):
    // bleeds in from the bottom of a light card, so no introArtTone.
    introArt: "/art/civic-hero.webp",
    introArtClearance: 17.6,
    landingArt: "/art/landing/civic.webp",
  },
]
